// Filling `{name}` placeholders in translated copy. Two rules, because the pages used two:
//
//   formatTemplate(t('lumen.table.range'), { from: 1, to: 10, total: 42 })   // '1 - 10 / 42'
//   formatTemplateFirst(t('lumen.common.total'), { n: total.toLocaleString() })
//
// - `formatTemplate` is the rule for new code: every occurrence, one pass, values inserted as they are.
// - `formatTemplateFirst` is exactly a `t(key).replace('{a}', a).replace('{b}', b)` chain, so a page can drop the
//   chain without changing a character it renders. Pass the values in the chain's order.

export type TemplateValues = Record<string, string | number>;

/**
 * Every `{name}` in `template` becomes `values[name]`, in one pass: a value is inserted literally (no `$`
 * patterns) and is never scanned for placeholders itself. Placeholders without a value stay as they are.
 *
 * `'{from} - {to} / {total}'` + `{ from: 1, to: 10, total: 42 }` → `'1 - 10 / 42'`.
 */
export function formatTemplate(template: string, values: TemplateValues): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (name in values ? String(values[name]) : match));
}

/**
 * Byte for byte what the chain `template.replace('{a}', a).replace('{b}', b)…` gives, for the keys of `values`
 * in their order:
 * - only the **first** occurrence of each placeholder is replaced;
 * - the replacements run one after another, so a value that contains a later key's `{placeholder}` is filled by
 *   that later key;
 * - `String.prototype.replace`'s patterns apply inside a value (`$$` → `$`, `$&` → the placeholder, `` $` `` and
 *   `$'` → the text before or after it).
 *
 * Use it to retire such a chain without a visible change. New code uses {@link formatTemplate}.
 */
export function formatTemplateFirst(template: string, values: TemplateValues): string {
  return Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, String(value)), template);
}
